import { useState, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  Body1,
  Body2,
  Badge,
  Button,
  Dropdown,
  Option,
  Spinner,
  MessageBar,
  MessageBarBody,
  DataGrid,
  DataGridHeader,
  DataGridRow,
  DataGridHeaderCell,
  DataGridBody,
  DataGridCell,
  createTableColumn,
  type TableColumnDefinition,
} from '@fluentui/react-components';
import { ArrowSyncRegular } from '@fluentui/react-icons';
import type {
  RuntimeBlindSpot,
  BlindSpotSeverity,
  BlindSpotStatus,
  BlindSpotListFilter,
} from '../types';
import { useRuntimeBlindSpotsList, useRecalculateBlindSpots } from '../hooks';
import {
  formatBlindSpotType,
  getSeverityBadgeColor,
  getStatusBadgeColor,
} from '../utils/scoring';
import { format } from 'date-fns';

interface BlindSpotListViewProps {
  onSelectBlindSpot: (blindSpot: RuntimeBlindSpot) => void;
}

const useStyles = makeStyles({
  filterBar: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalM,
    flexWrap: 'wrap',
  },
  dropdown: {
    minWidth: '150px',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
});

const SEVERITY_OPTIONS: BlindSpotSeverity[] = ['Critical', 'High', 'Medium', 'Low'];
const STATUS_OPTIONS: BlindSpotStatus[] = ['Open', 'Acknowledged', 'Mitigated', 'Resolved'];

/** Type values that may come from the API in PascalCase. */
const TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'RepoNotIntegrated', label: 'Repo Not Integrated' },
  { value: 'PipelineNotMonitored', label: 'Pipeline Not Monitored' },
  { value: 'EnvNotScanned', label: 'Environment Not Scanned' },
  { value: 'SecretsNotRotated', label: 'Secrets Not Rotated' },
  { value: 'IacDriftDetected', label: 'IaC Drift Detected' },
  { value: 'DependencyUntracked', label: 'Dependency Untracked' },
  { value: 'ManualDeployDetected', label: 'Manual Deploy Detected' },
];

const columns: TableColumnDefinition<RuntimeBlindSpot>[] = [
  createTableColumn<RuntimeBlindSpot>({
    columnId: 'type',
    compare: (a, b) => a.blindSpotType.localeCompare(b.blindSpotType),
    renderHeaderCell: () => 'Type',
    renderCell: (item) => <Body2>{formatBlindSpotType(item.blindSpotType)}</Body2>,
  }),
  createTableColumn<RuntimeBlindSpot>({
    columnId: 'scope',
    compare: (a, b) => a.scopeId.localeCompare(b.scopeId),
    renderHeaderCell: () => 'Scope',
    renderCell: (item) => (
      <Body2>{item.scopeType}: {item.scopeId}</Body2>
    ),
  }),
  createTableColumn<RuntimeBlindSpot>({
    columnId: 'severity',
    compare: (a, b) => {
      const order: Record<string, number> = { Critical: 0, High: 1, Medium: 2, Low: 3 };
      return (order[a.severity] ?? 4) - (order[b.severity] ?? 4);
    },
    renderHeaderCell: () => 'Severity',
    renderCell: (item) => (
      <Badge appearance="tint" color={getSeverityBadgeColor(item.severity)} size="medium">
        {item.severity}
      </Badge>
    ),
  }),
  createTableColumn<RuntimeBlindSpot>({
    columnId: 'confidence',
    compare: (a, b) => a.confidence - b.confidence,
    renderHeaderCell: () => 'Confidence',
    renderCell: (item) => <Body2>{item.confidence}%</Body2>,
  }),
  createTableColumn<RuntimeBlindSpot>({
    columnId: 'firstSeen',
    compare: (a, b) => a.firstSeenAt.localeCompare(b.firstSeenAt),
    renderHeaderCell: () => 'First Seen',
    renderCell: (item) => (
      <Body2>{format(new Date(item.firstSeenAt), 'MMM d, yyyy')}</Body2>
    ),
  }),
  createTableColumn<RuntimeBlindSpot>({
    columnId: 'lastSeen',
    compare: (a, b) => b.lastSeenAt.localeCompare(a.lastSeenAt),
    renderHeaderCell: () => 'Last Seen',
    renderCell: (item) => (
      <Body2>{format(new Date(item.lastSeenAt), 'MMM d, yyyy')}</Body2>
    ),
  }),
  createTableColumn<RuntimeBlindSpot>({
    columnId: 'status',
    compare: (a, b) => a.status.localeCompare(b.status),
    renderHeaderCell: () => 'Status',
    renderCell: (item) => (
      <Badge appearance="tint" color={getStatusBadgeColor(item.status)} size="medium">
        {item.status}
      </Badge>
    ),
  }),
];

export function BlindSpotListView({ onSelectBlindSpot }: BlindSpotListViewProps) {
  const styles = useStyles();
  const [filter, setFilter] = useState<BlindSpotListFilter>({});
  const { data: blindSpots, isLoading, error } = useRuntimeBlindSpotsList(filter);
  const recalculate = useRecalculateBlindSpots();

  const handleSeverityChange = useCallback((_: unknown, data: { optionValue?: string }) => {
    setFilter((prev) => ({ ...prev, severity: (data.optionValue || undefined) as BlindSpotSeverity | undefined }));
  }, []);

  const handleStatusChange = useCallback((_: unknown, data: { optionValue?: string }) => {
    setFilter((prev) => ({ ...prev, status: (data.optionValue || undefined) as BlindSpotStatus | undefined }));
  }, []);

  const handleTypeChange = useCallback((_: unknown, data: { optionValue?: string }) => {
    setFilter((prev) => ({ ...prev, type: (data.optionValue || undefined) as string | undefined }));
  }, []);

  if (error) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>Failed to load blind spots: {(error as Error).message}</MessageBarBody>
      </MessageBar>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.filterBar}>
        <Dropdown
          className={styles.dropdown}
          placeholder="Severity"
          onOptionSelect={handleSeverityChange}
          clearable
        >
          {SEVERITY_OPTIONS.map((s) => (
            <Option key={s} value={s}>{s}</Option>
          ))}
        </Dropdown>
        <Dropdown
          className={styles.dropdown}
          placeholder="Status"
          onOptionSelect={handleStatusChange}
          clearable
        >
          {STATUS_OPTIONS.map((s) => (
            <Option key={s} value={s}>{s}</Option>
          ))}
        </Dropdown>
        <Dropdown
          className={styles.dropdown}
          placeholder="Type"
          onOptionSelect={handleTypeChange}
          clearable
        >
          {TYPE_OPTIONS.map((t) => (
            <Option key={t.value} value={t.value}>{t.label}</Option>
          ))}
        </Dropdown>
        <Button
          appearance="outline"
          icon={<ArrowSyncRegular />}
          onClick={() => recalculate.mutate()}
          disabled={recalculate.isPending}
        >
          {recalculate.isPending ? 'Recalculating...' : 'Recalculate'}
        </Button>
      </div>

      {isLoading ? (
        <Spinner label="Loading blind spots..." />
      ) : (
        <DataGrid
          items={blindSpots ?? []}
          columns={columns}
          sortable
          getRowId={(item) => String(item.id)}
        >
          <DataGridHeader>
            <DataGridRow>
              {({ renderHeaderCell }) => (
                <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
              )}
            </DataGridRow>
          </DataGridHeader>
          <DataGridBody<RuntimeBlindSpot>>
            {({ item, rowId }) => (
              <DataGridRow<RuntimeBlindSpot>
                key={rowId}
                onClick={() => onSelectBlindSpot(item)}
                style={{ cursor: 'pointer' }}
              >
                {({ renderCell }) => (
                  <DataGridCell>{renderCell(item)}</DataGridCell>
                )}
              </DataGridRow>
            )}
          </DataGridBody>
        </DataGrid>
      )}

      {!isLoading && blindSpots && blindSpots.length === 0 && (
        <Body1 style={{ textAlign: 'center', padding: tokens.spacingVerticalXXL, color: tokens.colorNeutralForeground3 }}>
          No blind spots match the current filters.
        </Body1>
      )}
    </div>
  );
}
